import dayjs from 'dayjs';
import { View } from '@react-pdf/renderer';
import { DateUtils, Invoice, Supplier } from '../../../common';
import { en } from '../../theme/languages/en';
import { Span } from '../Span/Span';
import { DATE_FORMAT_SLASH } from '../../utils/invoiceTable';
import { getNetTermLabel } from '../../utils/general';
import { getInvoiceIdName } from '../../utils';
import { styles } from './InvoiceMetaData.module';

interface InvoiceMetaDataProps {
  invoiceData: Invoice;
  title: string;
  className?: {};
  showBillingPeriod?: boolean;
}

export const InvoiceMetaData = ({ title, invoiceData, className, showBillingPeriod }: InvoiceMetaDataProps) => {
  const dueDate =
    invoiceData?.customer &&
    dayjs(
      DateUtils.getDueDate(
        invoiceData?.issueDate || new Date(),
        isNaN(+invoiceData?.netTerms) ? invoiceData?.netTerms : +invoiceData?.netTerms,
        invoiceData?.customer?.country,
      ),
    ).format(DATE_FORMAT_SLASH);

  return (
    <View style={className}>
      <View style={styles.invoiceDetails}>
        {invoiceData?.poNumber && <Span style={styles.po}>{invoiceData?.poNumber}</Span>}
        <Span style={styles.mainTitle} canBeMissingValue missingValue={title}>
          {title}
        </Span>

        <View style={styles.detailsContainer}>
          <View style={styles.detailsSection}>
            <Span style={styles.detailsTitle}>{en.billTo}</Span>
            <Span style={styles.text}>{invoiceData?.customer?.legalName}</Span>
            <Span style={styles.text}>{invoiceData?.customer?.billingAddress}</Span>
            <Span style={styles.text}>{invoiceData?.customer?.city}</Span>
            <Span style={styles.text}>{invoiceData?.customer?.countryState}</Span>
            <Span style={styles.text}>{invoiceData?.customer?.country}</Span>
          </View>

          <View style={[styles.detailsSection, { marginLeft: 80 }]}>
            <View style={styles.textContainer}>
              <Span style={styles.detailsTitle}>{en.invoiceNo}</Span>
              <Span
                style={styles.text}
                canBeMissingValue
                missingValue={invoiceData?.documentNumber || invoiceData?.draftNumber}
                placeholder={en.draft}
              >
                {getInvoiceIdName(invoiceData)}
              </Span>
            </View>

            <View style={styles.textContainer}>
              <Span style={styles.text}>{`${en.issueDate}:`}</Span>
              <Span style={styles.text} canBeMissingValue missingValue={invoiceData?.issueDate} placeholder={'-'}>
                {dayjs(invoiceData?.issueDate).format(DATE_FORMAT_SLASH)}
              </Span>
            </View>

            <View style={styles.textContainer}>
              <Span style={styles.text}>{`${en.dueDate}:`}</Span>
              <Span style={styles.text} canBeMissingValue missingValue={dueDate} placeholder={'-'}>
                {dueDate}
                {!!+invoiceData?.netTerms && (
                  <Span style={styles.text} canBeMissingValue missingValue={invoiceData?.netTerms}>
                    {getNetTermLabel(invoiceData?.netTerms)}
                  </Span>
                )}
              </Span>
            </View>

            {showBillingPeriod && (
              <View style={styles.textContainer}>
                <Span style={styles.text}>{`${en.billingPeriod}:`}</Span>
                <Span
                  style={styles.text}
                  canBeMissingValue
                  missingValue={
                    invoiceData?.pricingModel?.tabs[0]?.billingStartDate ||
                    invoiceData?.pricingModel?.tabs[0]?.billingEndDate
                  }
                  placeholder={'-'}
                >{`${dayjs(invoiceData?.pricingModel?.tabs[0]?.billingStartDate).format(DATE_FORMAT_SLASH)} - ${dayjs(
                  invoiceData?.pricingModel?.tabs[0]?.billingEndDate,
                ).format(DATE_FORMAT_SLASH)}`}</Span>
              </View>
            )}
          </View>
        </View>
      </View>
    </View>
  );
};
